/**
 * Signing in to a local agent without leaving the app — the screen Claude Code and Cursor show for
 * their own sign-in: the browser opens on the agent's page, the address is here to copy in case it
 * did not, and a code can be pasted when the agent asks for one. Desktop only; the agent's sign-in
 * runs with no window behind it (lib/agentLogin.ts, agents.rs).
 *
 * Closing it any way — Back, Cancel, Escape, a click outside — ends the sign-in, so no process is
 * left waiting for a code nobody will paste.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { cleanCode, INITIAL_LOGIN, loginOutcome, readLoginLine, startAgentLogin, type LoginState, type RunningLogin } from '../lib/agentLogin'
import type { LocalAgentId } from '../lib/localAgent'
import { Modal } from './Modal'
import { CopyButton, ErrorBanner } from './primitives'
import { SignInOpener } from './SignInOpener'
import { browserOpen, browsersList } from '../lib/browsers'

type Phase = 'starting' | 'waiting' | 'sending' | 'ok' | 'failed'

/** How long "Connected" stays before the dialog closes itself. */
const SUCCESS_PAUSE_MS = 1500

export function AgentLoginDialog({
  agent,
  onClose,
  onSignedIn,
}: {
  agent: { id: LocalAgentId; name: string }
  onClose: () => void
  onSignedIn: () => void
}) {
  const { t } = useI18n()
  const [attempt, setAttempt] = useState(0)
  const [phase, setPhase] = useState<Phase>('starting')
  const [login, setLogin] = useState<LoginState>(INITIAL_LOGIN)
  const [code, setCode] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const running = useRef<RunningLogin | null>(null)
  // The latest state, for the output handlers, which are set up once per attempt.
  const loginRef = useRef<LoginState>(INITIAL_LOGIN)
  const signedIn = useRef(onSignedIn)
  useEffect(() => {
    signedIn.current = onSignedIn
  }, [onSignedIn])

  useEffect(() => {
    let gone = false
    setPhase('starting')
    setLogin(INITIAL_LOGIN)
    loginRef.current = INITIAL_LOGIN
    setProblem(null)
    setCode('')

    // Started after a tick: React runs this effect twice in development (mount, clean up, mount),
    // and two sign-ins started together would fight over the same agent. The first is cleaned up
    // before the tick, so only one starts.
    const timer = setTimeout(() => {
      startAgentLogin(agent.id, {
        onLine: (line) => {
          if (gone) return
          const before = loginRef.current
          const next = readLoginLine(before, line)
          loginRef.current = next
          setLogin(next)
          if (next.url || next.asksForCode) setPhase((p) => (p === 'starting' ? 'waiting' : p))
          // A failure printed after a pasted code: let the person paste again.
          if (next.error && next.error !== before.error) setPhase((p) => (p === 'sending' ? 'waiting' : p))
        },
        onDone: (exitCode) => {
          if (gone) return
          running.current = null
          const outcome = loginOutcome(loginRef.current, exitCode)
          if (outcome === 'ok') {
            setPhase('ok')
            signedIn.current()
          } else {
            setPhase('failed')
          }
        },
      })
        .then((started) => {
          // Closed (or restarted) while the sign-in was starting: end the one that just began.
          if (gone) void started.cancel()
          else running.current = started
        })
        .catch(() => {
          if (gone) return
          setPhase('failed')
          setProblem(t('agentLogin.startFailed'))
        })
    }, 0)

    return () => {
      gone = true
      clearTimeout(timer)
      const current = running.current
      running.current = null
      if (current) void current.cancel()
    }
    // `t` changes with the language; a new language is no reason to start the sign-in again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id, attempt])

  // "Connected" for a moment, then back to the list.
  useEffect(() => {
    if (phase !== 'ok') return
    const timer = setTimeout(onClose, SUCCESS_PAUSE_MS)
    return () => clearTimeout(timer)
  }, [phase, onClose])

  const close = useCallback(() => {
    // The effect's cleanup ends the sign-in when the dialog unmounts.
    onClose()
  }, [onClose])

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    const clean = cleanCode(code)
    if (!clean) {
      setProblem(t('agentLogin.invalidCode'))
      return
    }
    if (!running.current) return
    setProblem(null)
    setPhase('sending')
    try {
      await running.current.sendCode(clean)
    } catch {
      setPhase('waiting')
      setProblem(t('agentLogin.codeFailed'))
    }
  }

  const status =
    phase === 'starting' ? t('agentLogin.starting') : phase === 'sending' ? t('agentLogin.sending') : phase === 'waiting' ? t('agentLogin.waiting') : null
  const error = problem ?? (phase === 'failed' ? login.error ?? t('agentLogin.failed') : login.error)
  const showCode = login.asksForCode && phase !== 'ok' && phase !== 'failed'

  return (
    <Modal title={t('agentLogin.title')} onClose={close}>
      <form className="agent-login" onSubmit={(event) => void submit(event)}>
        <p className="tiny muted agent-login__for">{t('agentLogin.signingInTo', { agent: agent.name })}</p>

        {phase === 'ok' ? (
          <div className="connect__status connect__status--ok" role="status">
            <Check size={16} /> {t('agentLogin.success', { agent: agent.name })}
          </div>
        ) : (
          status && (
            <div className="agent-login__status small muted" role="status" aria-live="polite">
              <Loader2 size={14} className="spin" /> {status}
            </div>
          )
        )}

        {login.url && phase !== 'ok' && (
          <div className="field">
            <span className="field__label">{t('agentLogin.visit')}</span>
            <div className="agent-login__url">
              <input
                className="input mono"
                readOnly
                value={login.url}
                aria-label={t('agentLogin.urlLabel')}
                onFocus={(event) => event.currentTarget.select()}
              />
              <CopyButton text={login.url} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
            </div>
            {/* Another account of this agent: the browser remembers the one signed in (the owner, 21/10/2026). */}
            <SignInOpener url={login.url} list={browsersList} open={browserOpen} />
          </div>
        )}

        {showCode && (
          <label className="field">
            <span className="field__label">{t('agentLogin.pasteCode')}</span>
            <input
              className="input mono"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder={t('agentLogin.codePlaceholder')}
              autoComplete="off"
              spellCheck={false}
              disabled={phase === 'sending'}
            />
          </label>
        )}

        {error && phase !== 'ok' && <ErrorBanner message={error} />}

        {phase !== 'ok' && (
          <div className="agent-login__actions">
            {showCode && (
              <button type="submit" className="btn btn--primary" disabled={!code.trim() || phase === 'sending'}>
                {t('agentLogin.continue')}
              </button>
            )}
            {phase === 'failed' && (
              <button type="button" className="btn btn--primary" onClick={() => setAttempt((n) => n + 1)}>
                {t('agentLogin.retry')}
              </button>
            )}
            <button type="button" className="btn" onClick={close}>
              {t('common.back')}
            </button>
            <button type="button" className="btn btn--ghost" onClick={close}>
              {t('common.cancel')}
            </button>
          </div>
        )}
      </form>
    </Modal>
  )
}
